// src/App.jsx
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TabsProvider } from "./tabs/TabContext";


import AppLayout from "./layouts/AppLayout";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import CalendarGeneral from "./pages/CalendarGeneral"; // (지금은 미사용이지만, 다른 부분은 건드리지 않기 위해 유지)

import ExpenseEnvTotalDraftPreviewWindow from "./pages/expense/env/total/ExpenseEnvTotalDraftPreviewWindow.jsx"; // 통합 작성 페이지 PDF 미리보기 팝업(레이아웃 없이 단독 렌더)
import ExpenseEnvDisinfectionDraftPreviewWindow from "./pages/expense/env/disinfection/ExpenseEnvDisinfectionDraftPreviewWindow.jsx"; // 전 사업 확산 [2] — 환경/소독 통합 작성 페이지 PDF 미리보기 팝업
import ExpenseBusinessDraftPreviewWindow from "./pages/expense/business/ExpenseBusinessDraftPreviewWindow.jsx"; // 전 사업 확산 [3] — 경영팀 통합 작성 페이지 PDF 미리보기 팝업
import ExpenseServiceDraftPreviewWindow from "./pages/expense/service/ExpenseServiceDraftPreviewWindow.jsx"; // 전 사업 확산 [4] — 용역사업 통합 작성 페이지 PDF 미리보기 팝업
import ExpenseCafeGasanDraftPreviewWindow from "./pages/expense/cafe/ExpenseCafeGasanDraftPreviewWindow.jsx"; // 전 사업 확산 [5] — 카페인 가산점 통합 작성 페이지 PDF 미리보기 팝업
import ExpenseCafeDoksanDraftPreviewWindow from "./pages/expense/cafe/ExpenseCafeDoksanDraftPreviewWindow.jsx"; // 전 사업 확산 [5] — 카페인 독산점 통합 작성 페이지 PDF 미리보기 팝업
import ExpenseCafeGeumnaraeDraftPreviewWindow from "./pages/expense/cafe/ExpenseCafeGeumnaraeDraftPreviewWindow.jsx"; // 전 사업 확산 [5] — 카페인 금나래점 통합 작성 페이지 PDF 미리보기 팝업
import ExpenseCafeGreenDraftPreviewWindow from "./pages/expense/cafe/ExpenseCafeGreenDraftPreviewWindow.jsx"; // 전 사업 확산 [5] — 카페인 그린점 통합 작성 페이지 PDF 미리보기 팝업
import IncomeBusinessDraftPreviewWindow from "./pages/income/business/IncomeBusinessDraftPreviewWindow.jsx"; // 수입데이터+수입기안 통합 작성 [파일럿] — 경영팀 미리보기 팝업
import IncomeEnvTotalDraftPreviewWindow from "./pages/income/env/total/IncomeEnvTotalDraftPreviewWindow.jsx"; // 수입데이터+수입기안 통합 작성 [2단계] — 환경/종합 미리보기 팝업
import IncomeEnvDisinfectionDraftPreviewWindow from "./pages/income/env/disinfection/IncomeEnvDisinfectionDraftPreviewWindow.jsx"; // 수입데이터+수입기안 통합 작성 [3단계] — 환경/소독 미리보기 팝업
import IncomeServiceDraftPreviewWindow from "./pages/income/service/IncomeServiceDraftPreviewWindow.jsx"; // 수입데이터+수입기안 통합 작성 [4단계] — 용역사업 미리보기 팝업

// ✅ 동적(:id) 파라미터 라우트 전용 — useParams()로 URL의 id를 읽으므로 keep-alive
// 뱅크(라우트 매칭 컨텍스트 밖)에 넣을 수 없어 protectedRoutes.js로 옮기지 않고
// 여기 그대로 남겨둠(routes/protectedRoutes.js의 EXCLUDED_DYNAMIC_ROUTES 주석 참고).
import DraftDocumentNew from "./pages/draft_document/DraftDocumentNew"; // 기안서 작성(수정/조회 겸용, 문서종류 전체 지원)
import DraftDocumentDetail from "./pages/draft_document/DraftDocumentDetail";
import BudgetYearlyDetail from "./pages/accounting/budget/BudgetYearlyDetail";
import EvaluationManualViewer from "./pages/evaluation/EvaluationManualViewer";
import EvaluationIndicatorBoard from "./pages/evaluation/EvaluationIndicatorBoard";
import RentalOrderDetail from "./pages/rental/RentalOrderDetail.jsx"; // 금천아 빌려줘 — 주문 상세

import RequireAuth from "./routes/RequireAuth";
import { isTokenValid, getAccessToken, getRefreshToken } from "./auth";

import { PROTECTED_ROUTES } from "./routes/protectedRoutes";

/* ──────────────────────────────────────────────────────────────
   🔎 디버깅 유틸: “컴포넌트 자리에 객체가 들어갔는지” 즉시 확인
   - React.memo 등은 'object'도 될 수 있지만, 일반 객체와 구분되는 $$typeof를 가집니다.
   - 일반 객체가 들어오면 콘솔에 빨간 줄로 바로 표시됩니다.
────────────────────────────────────────────────────────────── */
function isProbablyValidComponentType(Comp) {
  return (
    typeof Comp === "function" ||
    (Comp && typeof Comp === "object" && "$$typeof" in Comp)
  );
}

function assertComponentType(name, Comp) {
  const ok = isProbablyValidComponentType(Comp);
  // 콘솔에서 한눈에 보이도록
  // eslint-disable-next-line no-console
  console.log(`[check] ${name}:`, {
    type: typeof Comp,
    hasTypeofSymbol: Comp ? "$$typeof" in Comp : false,
    value: Comp,
  });
  if (!ok) {
    // eslint-disable-next-line no-console
    console.error(
      `⛔ "${name}"가 유효한 컴포넌트 타입이 아닙니다. import/export를 확인하세요.`
    );
  }
  return ok;
}

// 보호 구간(RequireAuth+AppLayout)을 잠시 끄고 공개 구간만 확인하고 싶을 때 false로
const ENABLE_PROTECTED = true;

export default function App() {
  // ✅ 변경: access 유효 OR refresh 유효 → 로그인 상태로 간주
  const authed =
    isTokenValid(getAccessToken()) || isTokenValid(getRefreshToken());

  // 👇 최초 렌더 시 의심되는 대상 전부 점검(공개 구간 + 동적 라우트 전용 컴포넌트만 —
  // PROTECTED_ROUTES에 옮겨진 나머지 페이지들은 routes/protectedRoutes.js 자체의
  // 배열 유효성 체크로 대체됨)
  assertComponentType("TabsProvider", TabsProvider);
  assertComponentType("AppLayout", AppLayout);
  assertComponentType("RequireAuth", RequireAuth);
  assertComponentType("Home", Home);
  assertComponentType("Login", Login);
  assertComponentType("Signup", Signup);
  assertComponentType("CalendarGeneral", CalendarGeneral);
  assertComponentType("DraftDocumentNew", DraftDocumentNew);
  assertComponentType("DraftDocumentDetail", DraftDocumentDetail);
  assertComponentType("BudgetYearlyDetail", BudgetYearlyDetail);
  assertComponentType("EvaluationManualViewer", EvaluationManualViewer);
  assertComponentType("EvaluationIndicatorBoard", EvaluationIndicatorBoard);

  return (
    <TabsProvider>
      <BrowserRouter>
        <Routes>
          {/* ── 헤더 없는 공개 구간 ───────────────────────── */}
          <Route
            path="/"
            element={authed ? <Navigate to="/dashboard" replace /> : <Home />}
          />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* ✅ 환경/종합 통합 작성 페이지 PDF 미리보기 팝업 — AppLayout(사이드바/탭) 없이
              단독 렌더. 인증은 필요하므로 RequireAuth만 별도로 감쌈(로그인/회원가입과 달리
              업무 데이터를 보여주므로). */}
          <Route
            path="/expense/env/total/draft/preview"
            element={
              <RequireAuth>
                <ExpenseEnvTotalDraftPreviewWindow />
              </RequireAuth>
            }
          />
          {/* ✅ 전 사업 확산 [2] — 환경/소독 통합 작성 페이지 PDF 미리보기 팝업(위와 동일 이유) */}
          <Route
            path="/expense/env/disinfection/draft/preview"
            element={
              <RequireAuth>
                <ExpenseEnvDisinfectionDraftPreviewWindow />
              </RequireAuth>
            }
          />
          {/* ✅ 전 사업 확산 [3] — 경영팀 통합 작성 페이지 PDF 미리보기 팝업(위와 동일 이유) */}
          <Route
            path="/expense/business/draft/preview"
            element={
              <RequireAuth>
                <ExpenseBusinessDraftPreviewWindow />
              </RequireAuth>
            }
          />

          {/* ✅ 수입데이터+수입기안 통합 작성 [파일럿] — 경영팀 미리보기 팝업(위와 동일 이유) */}
          <Route
            path="/income/business/draft/preview"
            element={
              <RequireAuth>
                <IncomeBusinessDraftPreviewWindow />
              </RequireAuth>
            }
          />

          {/* ✅ 수입데이터+수입기안 통합 작성 [2단계] — 환경/종합 미리보기 팝업(위와 동일 이유) */}
          <Route
            path="/income/env/total/draft/preview"
            element={
              <RequireAuth>
                <IncomeEnvTotalDraftPreviewWindow />
              </RequireAuth>
            }
          />

          {/* ✅ 수입데이터+수입기안 통합 작성 [3단계] — 환경/소독 미리보기 팝업(위와 동일 이유) */}
          <Route
            path="/income/env/disinfection/draft/preview"
            element={
              <RequireAuth>
                <IncomeEnvDisinfectionDraftPreviewWindow />
              </RequireAuth>
            }
          />

          {/* ✅ 전 사업 확산 [4] — 용역사업 통합 작성 페이지 PDF 미리보기 팝업(위와 동일 이유) */}
          <Route
            path="/expense/service/draft/preview"
            element={
              <RequireAuth>
                <ExpenseServiceDraftPreviewWindow />
              </RequireAuth>
            }
          />

          {/* ✅ 수입데이터+수입기안 통합 작성 [4단계] — 용역사업 미리보기 팝업(위와 동일 이유) */}
          <Route
            path="/income/service/draft/preview"
            element={
              <RequireAuth>
                <IncomeServiceDraftPreviewWindow />
              </RequireAuth>
            }
          />

          {/* ✅ 전 사업 확산 [5] — 카페 4개점 통합 작성 페이지 PDF 미리보기 팝업(위와 동일 이유) */}
          <Route
            path="/expense/cafe/gasan/draft/preview"
            element={
              <RequireAuth>
                <ExpenseCafeGasanDraftPreviewWindow />
              </RequireAuth>
            }
          />
          <Route
            path="/expense/cafe/doksan/draft/preview"
            element={
              <RequireAuth>
                <ExpenseCafeDoksanDraftPreviewWindow />
              </RequireAuth>
            }
          />
          <Route
            path="/expense/cafe/geumnarae/draft/preview"
            element={
              <RequireAuth>
                <ExpenseCafeGeumnaraeDraftPreviewWindow />
              </RequireAuth>
            }
          />
          <Route
            path="/expense/cafe/green/draft/preview"
            element={
              <RequireAuth>
                <ExpenseCafeGreenDraftPreviewWindow />
              </RequireAuth>
            }
          />

          {/* ── 헤더 있는 보호 구간(레이아웃 적용) ─────────── */}
          {ENABLE_PROTECTED ? (
            <Route
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            >
              {/* ✅ 정적 페이지 전부 — routes/protectedRoutes.js 단일 소스에서 생성.
                  AppLayout.jsx의 keep-alive 뱅크가 같은 배열을 쓰므로 여기 추가하면
                  라우팅+keep-alive 등록이 동시에 된다(등록 누락 구조적으로 불가능). */}
              {PROTECTED_ROUTES.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}

              {/* ✅ 기안서 관리 작성 — 문서종류별 분리 전 경로. 즐겨찾기/기존 탭 보호를 위해 지출기안 페이지로 리다이렉트 */}
              <Route path="/approval/draftdocument/new" element={<Navigate to="/approval/expense-draft/new" replace />} />

              {/* ✅ 동적(:id) 라우트 — keep-alive 대상 아님(useParams 컨텍스트 이슈로 이번 확대에서 제외, protectedRoutes.js 주석 참고) */}
              <Route path="/approval/draftdocument/:id/edit" element={<DraftDocumentNew />} />
              <Route path="/approval/draftdocument/:id" element={<DraftDocumentDetail />} />
              <Route path="/accounting/budget/yearly/:id" element={<BudgetYearlyDetail />} />
              <Route path="/evaluation/manuals/:id/viewer" element={<EvaluationManualViewer />} />
              <Route path="/evaluation/manuals/:id/indicators" element={<EvaluationIndicatorBoard />} />
              <Route path="/rental/orders/:id" element={<RentalOrderDetail />} />

              {/* 예전 짧은 경로 호환 리다이렉트 */}
              <Route
                path="/income/env/disinf/new"
                element={<Navigate to="/income/env/disinfection/new" replace />}
              />
            </Route>
          ) : null}

          {/* 🔁 변경 포인트: 404 대신 홈으로 리다이렉트(SPA 진입 보정) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </TabsProvider>
  );
}
